import { CHANNEL_ID, MEMBER_ID } from '@@types/common';
import MemberLang from '@@types/MemberLang';

import { channelsStore } from '~/stores/ChannelsStore';
import { globalSettingStore } from '~/stores/GlobalSettingStore';
import { uiState } from '~/stores/UiState';
import { getChannelLangPreference } from '~/utils/channelUtil';
import { isEmpty, noop } from 'lodash';
import { action, computed, observable } from 'mobx';
import { curry, map, pipe } from 'ramda';
import groupBy from 'ramda/es/groupBy';
import head from 'ramda/es/head';
import length from 'ramda/es/length';
import lt from 'ramda/es/lt';
import prop from 'ramda/es/prop';
import uniq from 'ramda/es/uniq';
import ramdaUseWith from 'ramda/es/useWith';
import values from 'ramda/es/values';

import { api } from './providers/Api2';

class ChannelMembersLangStore {
  @observable
  channelMembersLangMap = {};

  @computed
  get isMultiLanguages() {
    return pipe(values, uniq, ramdaUseWith(lt(0), [length]))(this.channelMembersLangMap);
  }

  findMemberLang = curry(
    (channelId: CHANNEL_ID, memberId: MEMBER_ID, { useFallback = false }: { useFallback?: boolean } = {}) => {
      const lang = this.channelMembersLangMap?.[channelId]?.[memberId];
      if (lang) {
        return lang;
      }
      if (!useFallback) {
        return;
      }
      const channel = channelsStore.getById(channelId);
      return getChannelLangPreference(channel) ?? globalSettingStore.lang ?? '';
    }
  );

  fetchAndCache = (channelId) => {
    api.channel.fetchChannelMembersLang(channelId).then(this.setFetchResult, noop);
  };

  @action
  setFetchResult = (result) => {
    if (isEmpty(result)) return;
    const { members, channelId } = result;

    this.channelMembersLangMap = uiState.pickAndSetLruChannelIds(
      channelId,
      pipe<any, any, any>(groupBy<MemberLang>(prop('memberId')), map(ramdaUseWith(prop('lang'), [head])))(members)
    )(this.channelMembersLangMap);
  };

  @action
  remove = curry((channelId, memberId) => {
    if (!this.channelMembersLangMap[channelId]) {
      return;
    }
    delete this.channelMembersLangMap[channelId][memberId];
  });

  @action
  removeMembers = curry((channelId, membersId) => {
    map(this.remove(channelId), membersId);
  });

  @action
  update = ({ channelId, memberId, lang }: MemberLang) => {
    if (!this.channelMembersLangMap[channelId]) {
      return;
    }
    this.channelMembersLangMap[channelId][memberId] = lang;
  };
}

export default ChannelMembersLangStore;

export const channelMembersLangStore = new ChannelMembersLangStore();
